import i18n, { type Resource, type ResourceLanguage } from 'i18next'
import { initReactI18next } from 'react-i18next'
import { PENDING_LOCALES } from './languages'

/**
 * One locale at a time, loaded on demand.
 *
 * The ten locales used to be ten static imports, and each one is a folder of
 * twenty namespace files — **two hundred module requests on every page load**,
 * for nine languages the person will never see. In a production build that is
 * dead weight in the bundle; in dev, where Vite serves every module separately,
 * it was two hundred of the ~520 requests a cold load makes.
 *
 * Through the ngrok tunnel that volume was enough to make some of them answer
 * 503. A single failed module breaks the whole ES module graph, so the screen
 * came up blank with no exception in the console — only "Failed to load
 * resource" lines, which is the hardest kind of failure to read.
 *
 * `import.meta.glob` without `eager` gives one dynamic importer per locale, so
 * only the active language (plus the fallback) is ever fetched. Switching
 * language loads the new one and hands it to i18next before the switch lands.
 */
const LOADERS = import.meta.glob('../locales/*/index.ts') as Record<
  string,
  () => Promise<{ default: ResourceLanguage }>
>

const localeKey = (locale: string) => `../locales/${locale}/index.ts`

const FALLBACK = 'en-US'

/**
 * Regional variants that read their base locale's strings. `de-AT` and `de-CH`
 * still have folders of their own (the API's fifteen locales leaking into the
 * app's ten), but the bundle loaded for them is the base one; the French
 * variants have no folder at all. The code itself is kept as the language, so
 * dates and numbers come out in the regional format and the picker shows the
 * variant the person chose.
 */
const REGIONAL_BASE: Record<string, string> = {
  'de-AT': 'de-DE',
  'de-CH': 'de-DE',
  'fr-BE': 'fr-FR',
  'fr-CA': 'fr-FR',
  'fr-CH': 'fr-FR'
}

const isPending = (locale: string) => PENDING_LOCALES.includes(locale)

const SUPPORTED = [
  ...Object.keys(LOADERS).map((p) => p.split('/')[2]),
  ...Object.keys(REGIONAL_BASE)
]
  .filter((locale, index, all) => all.indexOf(locale) === index && !isPending(locale))
  .sort()

/**
 * Maps whatever we are handed — a browser code, a stored preference — onto the
 * locale whose bundle we load. A regional variant maps onto its base, so a
 * browser set to Austrian German reads German rather than English. Locales
 * still being translated (`PENDING_LOCALES`) map onto nothing.
 */
const LANGUAGE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries({
    en: 'en-US',
    'en-US': 'en-US',
    pt: 'pt-BR',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-PT',
    es: 'es-ES',
    'es-ES': 'es-ES',
    'es-MX': 'es-MX',
    'es-AR': 'es-AR',
    it: 'it-IT',
    'it-IT': 'it-IT',
    de: 'de-DE',
    'de-DE': 'de-DE',
    fr: 'fr-FR',
    'fr-FR': 'fr-FR',
    ...REGIONAL_BASE
  }).filter(([code, locale]) => !isPending(code) && !isPending(locale))
)

const normalizeToFullLocale = (locale: string): string => {
  if (!locale) return FALLBACK
  if (LANGUAGE_MAP[locale]) return LANGUAGE_MAP[locale]
  const short = locale.split('-')[0]
  if (LANGUAGE_MAP[short]) return LANGUAGE_MAP[short]
  return FALLBACK
}

/** The language to run i18next in: a known regional variant as is, anything else normalized. */
const toLanguage = (locale: string): string =>
  REGIONAL_BASE[locale] && LANGUAGE_MAP[locale] ? locale : normalizeToFullLocale(locale)

/**
 * A language someone asked for explicitly (the `?lang=` of a public link, the
 * `data-lang` of `/embed.js`), mapped onto one of our locales; `null` when it
 * is none of them, so the caller falls back instead of landing on English.
 */
export const resolveSupportedLocale = (locale: string | null | undefined): string | null => {
  if (!locale) return null
  const code = locale.trim().replace('_', '-')
  const exact = Object.keys(LANGUAGE_MAP).find((k) => k.toLowerCase() === code.toLowerCase())
  if (exact) return LANGUAGE_MAP[exact]
  return LANGUAGE_MAP[code.split('-')[0].toLowerCase()] ?? null
}

const detectBrowserLanguage = (): string =>
  normalizeToFullLocale(navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage || '')

const getInitialLanguage = (): string => {
  try {
    const stored = localStorage.getItem('i18nextLng')
    if (stored) {
      const language = toLanguage(stored)
      if (language !== stored) localStorage.setItem('i18nextLng', language)
      return language
    }
    const detected = detectBrowserLanguage()
    localStorage.setItem('i18nextLng', detected)
    return detected
  } catch {
    /* Private window, or site data blocked. Not a reason to fail booting. */
    return FALLBACK
  }
}

/** Fetches a locale's namespaces and registers them. A no-op if already in. */
const loadLocale = async (locale: string): Promise<void> => {
  if (i18n.hasResourceBundle?.(locale, 'common')) return
  const load = LOADERS[localeKey(locale)]
  if (!load) return
  try {
    const mod = await load()
    for (const [namespace, resource] of Object.entries(mod.default ?? {})) {
      i18n.addResourceBundle(locale, namespace, resource, true, true)
    }
  } catch (error) {
    console.error(`i18n: failed to load locale ${locale}`, error)
  }
}

const initialLanguage = getInitialLanguage()

/**
 * The initial bundles are read before `init` so the first render already has
 * its strings. With `useSuspense: false` a late bundle would paint raw keys
 * first and the real text a frame later.
 */
const loadInitialResources = async (): Promise<Resource> => {
  const resources: Resource = {}
  for (const locale of new Set([normalizeToFullLocale(initialLanguage), FALLBACK])) {
    const load = LOADERS[localeKey(locale)]
    if (!load) continue
    try {
      resources[locale] = (await load()).default
    } catch (error) {
      console.error(`i18n: failed to load locale ${locale}`, error)
    }
  }
  return resources
}

/* The namespaces are the files of the locale folder (see the index.ts of each locale),
   so a module's namespace needs no entry here. */
const namespacesOf = (resources: Record<string, Record<string, unknown>>): string[] =>
  Object.keys(resources[FALLBACK] ?? Object.values(resources)[0] ?? { common: {} })

/**
 * Boot is a promise, not a top-level await.
 *
 * The build targets browsers without top-level await (chrome87, safari14), so
 * the module cannot block on the first bundle by itself. `main.tsx` waits on
 * this before mounting, which is the same guarantee without the syntax.
 */
export const i18nReady: Promise<unknown> = loadInitialResources().then((resources) =>
  i18n
  .use(initReactI18next)
  .init({
    resources,
    /* A regional variant reads its base before English. */
    fallbackLng: (code: string) => (REGIONAL_BASE[code] ? [REGIONAL_BASE[code], FALLBACK] : [FALLBACK]),
    defaultNS: 'common',
    ns: namespacesOf(resources),
    lng: initialLanguage,
    load: 'currentOnly',
    supportedLngs: SUPPORTED,
    nonExplicitSupportedLngs: false,
    cleanCode: true,

    /* Every locale but the current one is still on disk, not in memory. Without
       this, i18next declares a key missing the instant a language is selected
       and before its bundle lands. */
    partialBundledLanguages: true,

    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    saveMissing: false,

    debug: false
  })
  .catch((error) => {
    console.error('i18n initialization error:', error)
  })
)

/**
 * A language change may point at a locale that was never fetched.
 *
 * `languageChanging` fires before the switch takes effect, which is the only
 * hook where the bundle can still arrive in time. The `languageChanged` pass
 * afterwards is the safety net for a load that lost the race — it re-renders
 * once the strings are in rather than leaving raw keys on screen.
 */
i18n.on('languageChanging', (lng: string) => {
  void loadLocale(normalizeToFullLocale(lng))
})

i18n.on('languageChanged', (lng: string) => {
  const normalized = normalizeToFullLocale(lng)
  try {
    localStorage.setItem('i18nextLng', toLanguage(lng))
  } catch {
    /* Storage unavailable — the choice just does not survive the reload. */
  }
  if (!i18n.hasResourceBundle(normalized, 'common')) {
    void loadLocale(normalized).then(() => {
      if (i18n.hasResourceBundle(normalized, 'common')) void i18n.changeLanguage(lng)
    })
  }
})

export { loadLocale }
export default i18n
